import { Link } from "react-router-dom";

const sameDay = (a, b) =>
  a.getFullYear() === b.getFullYear() && a.getMonth() === b.getMonth() && a.getDate() === b.getDate();

const weekOf = (dates) => {
  const today = new Date();
  today.setHours(0, 0, 0, 0);
  return [...Array(7)].map((_, i) => {
    const day = new Date(today.getFullYear(), today.getMonth(), today.getDate() + i);
    return { day, count: dates.filter((d) => sameDay(new Date(d), day)).length };
  });
};

const WelcomeBanner = ({ greeting, summary, actions = [], deadlines = [], loading }) => {
  const week = weekOf(deadlines);
  const today = new Date().toLocaleDateString(undefined, {
    weekday: "long",
    day: "numeric",
    month: "long",
    year: "numeric",
  });

  return (
    <section className="relative overflow-hidden rounded-2xl bg-gradient-to-br from-brand-700 via-brand-600 to-indigo-500 p-5 text-white shadow-pop sm:p-6">
      <span aria-hidden className="pointer-events-none absolute -right-16 -top-20 h-64 w-64 rounded-full bg-white/10" />
      <span aria-hidden className="pointer-events-none absolute -bottom-24 right-40 h-48 w-48 rounded-full bg-white/5" />

      <div className="relative flex flex-col gap-6 lg:flex-row lg:items-end lg:justify-between">
        <div className="min-w-0">
          <p className="text-xs font-medium uppercase tracking-wider text-white/70">{today}</p>
          <h1 className="mt-1.5 text-2xl font-semibold tracking-tight">{greeting}</h1>
          {loading ? (
            <div className="mt-2 h-4 w-64 max-w-full animate-pulse rounded bg-white/20" />
          ) : (
            summary && <p className="mt-1.5 max-w-xl text-sm text-white/85">{summary}</p>
          )}

          {actions.length > 0 && (
            <div className="mt-4 flex flex-wrap gap-2">
              {actions.map(({ to, icon: Icon, label }) => (
                <Link
                  key={to}
                  to={to}
                  className="inline-flex items-center gap-1.5 rounded-lg bg-white/15 px-3 py-1.5 text-xs font-semibold text-white ring-1 ring-inset ring-white/20 transition hover:bg-white/25"
                >
                  {Icon && <Icon size={14} strokeWidth={2} />}
                  {label}
                </Link>
              ))}
            </div>
          )}
        </div>

        <div className="shrink-0">
          <p className="mb-2 text-[11px] font-semibold uppercase tracking-wider text-white/70">
            Deadlines this week
          </p>
          <ol className="grid grid-cols-7 gap-1.5">
            {week.map(({ day, count }, i) => (
              <li
                key={day.toISOString()}
                title={`${day.toLocaleDateString(undefined, { weekday: "long", day: "numeric", month: "short" })}: ${count} deadline${count === 1 ? "" : "s"}`}
                className={`flex w-11 flex-col items-center rounded-xl px-1 py-2 ${
                  i === 0 ? "bg-white text-brand-700" : "bg-white/10 text-white"
                }`}
              >
                <span className={`text-[10px] font-semibold uppercase ${i === 0 ? "text-brand-500" : "text-white/70"}`}>
                  {day.toLocaleDateString(undefined, { weekday: "short" })}
                </span>
                <span className="text-sm font-semibold leading-tight">{day.getDate()}</span>
                <span
                  className={`mt-1 min-w-[1.25rem] rounded-full px-1 text-center text-[10px] font-bold tabular-nums ${
                    loading
                      ? "text-transparent"
                      : count === 0
                      ? i === 0 ? "text-brand-300" : "text-white/40"
                      : i === 0 ? "bg-brand-600 text-white" : "bg-white text-brand-700"
                  }`}
                >
                  {count}
                </span>
              </li>
            ))}
          </ol>
        </div>
      </div>
    </section>
  );
};

export default WelcomeBanner;